import type { InvestigationActivity } from "$global-providers/rxdb-provider/collections/investigation-activity";
import type { DeepPartial } from "$routes/_lib/deep-partial";
import { clone } from "ramda";
import {
  isBacterialDiseaseIcd11Code,
  isGuidedDiagnosisDiseaseIcd11Code,
} from "../../consultation/_lib/bacterial-diseases-codes";
import { GENERAL_RECENCY_WINDOW_MS } from "./constants";
import { FHIRResourceTypes } from "./db/collections/common";
import {
  ClinicalResourceSubmissionStatus,
  type PatientClinicalResource,
} from "./db/collections/patient-clinical-resource";
export type DiseaseRecommendation = {
  id: string;
  diseaseDisplayName: string;
  investigations: {
    catalogueItemLoincCode: string;
    displayName: string;
    code: string;
  }[];
};
export type DiagnosisGuidance = {
  icd11Code: string;
  displayName: string;
} | null;
export const EXTERNAL_CODING_TO_URI_PREFIX = {
  snomed: "http://snomed.info/sct/",
  loinc: "http://loinc.org/",
};

export const getRemainingRecommendations = ({
  investigationCatalogueLoincCodes,
  recommendations,
}: {
  investigationCatalogueLoincCodes: Array<string>;
  recommendations: DiseaseRecommendation[];
}) => {
  const selectedLoincSet = new Set(investigationCatalogueLoincCodes);

  return recommendations.filter((recommendation) => {
    const diseaseLoincCodes = new Set(
      LoincCodesForHidingRecommendations[recommendation.id] || [],
    );
    return ![...selectedLoincSet].some((code) => diseaseLoincCodes.has(code));
  });
};

export const Findings = {
  fever: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}386661006`,
  blood_in_urine: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}34436003`,
  chest_pain: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}29857009`,
  cough: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}49727002`,
  diarrhoea: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}62315008`,
  breathless: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}267036007`,
  headache: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}25064002`,
  muscle_ache: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}68962001`,
  vomiting: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}249497008`,
  rash: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}271807003`,
  pain_on_passing_urine: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}49650001`,
  stomach_pain: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}21522001`,
} as const;

export type FindingType = (typeof Findings)[keyof typeof Findings];

export const Diseases = {
  malaria: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}61462000`,
  typhoid: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}4834000`,
  dengue: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}38362002`,
  uti: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}68566005`,
} as const;

export const ALLOWED_CATALOGUE_LOINC_CODES = {
  "malaria-rdt": "70569-9",
  "malaria-blood-smear": "32700-7",
  "salmonella-antigen-test": "101140-2",
  "widal-test": "53956-9",
  "typhoid-igg-igm-rapid-test": "17566-1",
  urinalysis: "24357-6",
  "dengue-fever-antibody-test": "75223-8",
};

export const ALLOWED_RESULT_LOINC_CODES = {
  "malaria-rdt": ALLOWED_CATALOGUE_LOINC_CODES["malaria-rdt"],
  "malaria-blood-smear-result": "32700-7-a",
  "salmonella-antigen-test":
    ALLOWED_CATALOGUE_LOINC_CODES["salmonella-antigen-test"],
  "salmonella-antigen-test-alternate": "17562-0",
  "typhoid-igm": "17566-1-b",
  "typhoid-igm-alternate": "9674-3",
  "urinalysis-nitrites": "5802-4",
  "urinalysis-leukocytes": "5799-2",
  "urinalysis-blood": "5794-3",
  "dengue-igg": "29661-6",
  "dengue-igm": "29663-2",
  "widal-test": "53956-9",
  "widal-test-alternate": "73672-8",
};

// Includes LOINC codes from duplicated investigations - remove once DB cleaned up
export const LoincCodesForHidingRecommendations = {
  [Diseases.malaria]: [
    ALLOWED_RESULT_LOINC_CODES["malaria-rdt"],
    ALLOWED_RESULT_LOINC_CODES["malaria-blood-smear-result"],
    ALLOWED_CATALOGUE_LOINC_CODES["malaria-blood-smear"],
  ],
  [Diseases.dengue]: [
    ALLOWED_RESULT_LOINC_CODES["dengue-igg"],
    ALLOWED_RESULT_LOINC_CODES["dengue-igm"],
    ALLOWED_CATALOGUE_LOINC_CODES["dengue-fever-antibody-test"],
  ],
  [Diseases.uti]: [
    ALLOWED_RESULT_LOINC_CODES["urinalysis-nitrites"],
    ALLOWED_RESULT_LOINC_CODES["urinalysis-leukocytes"],
    ALLOWED_RESULT_LOINC_CODES["urinalysis-blood"],
    ALLOWED_CATALOGUE_LOINC_CODES.urinalysis,
  ],
  [Diseases.typhoid]: [
    ALLOWED_RESULT_LOINC_CODES["salmonella-antigen-test"],
    ALLOWED_RESULT_LOINC_CODES["salmonella-antigen-test-alternate"],
    ALLOWED_RESULT_LOINC_CODES["typhoid-igm"],
    ALLOWED_RESULT_LOINC_CODES["typhoid-igm-alternate"],
    ALLOWED_RESULT_LOINC_CODES["widal-test"],
    ALLOWED_RESULT_LOINC_CODES["widal-test-alternate"],
    ALLOWED_CATALOGUE_LOINC_CODES["typhoid-igg-igm-rapid-test"],
    ALLOWED_CATALOGUE_LOINC_CODES["salmonella-antigen-test"],
    ALLOWED_CATALOGUE_LOINC_CODES["widal-test"],
  ],
};

export const ALLOWED_RESULT_TO_CATALOGUE_LOINC_CODES = {
  [ALLOWED_RESULT_LOINC_CODES["malaria-blood-smear-result"]]:
    ALLOWED_CATALOGUE_LOINC_CODES["malaria-blood-smear"],
  [ALLOWED_RESULT_LOINC_CODES["typhoid-igm"]]:
    ALLOWED_CATALOGUE_LOINC_CODES["typhoid-igg-igm-rapid-test"],
  [ALLOWED_RESULT_LOINC_CODES["urinalysis-nitrites"]]:
    ALLOWED_CATALOGUE_LOINC_CODES.urinalysis,
  [ALLOWED_RESULT_LOINC_CODES["urinalysis-leukocytes"]]:
    ALLOWED_CATALOGUE_LOINC_CODES.urinalysis,
  [ALLOWED_RESULT_LOINC_CODES["urinalysis-blood"]]:
    ALLOWED_CATALOGUE_LOINC_CODES.urinalysis,
  [ALLOWED_RESULT_LOINC_CODES["dengue-igg"]]:
    ALLOWED_CATALOGUE_LOINC_CODES["dengue-fever-antibody-test"],
  [ALLOWED_RESULT_LOINC_CODES["dengue-igm"]]:
    ALLOWED_CATALOGUE_LOINC_CODES["dengue-fever-antibody-test"],
};

export const snomedDiseaseToDiseaseRecommendation: Record<
  DiseaseType,
  DiseaseRecommendation
> = {
  [Diseases.malaria]: {
    id: Diseases.malaria,
    diseaseDisplayName: "Malaria",
    investigations: [
      {
        catalogueItemLoincCode: ALLOWED_CATALOGUE_LOINC_CODES["malaria-rdt"],
        displayName: "Malaria rapid diagnostic test",
        code: "391513009",
      },
      {
        catalogueItemLoincCode:
          ALLOWED_CATALOGUE_LOINC_CODES["malaria-blood-smear"],
        displayName: "Malaria blood smear",
        code: "12845003",
      },
    ],
  },
  [Diseases.typhoid]: {
    id: Diseases.typhoid,
    diseaseDisplayName: "Typhoid",
    investigations: [
      {
        catalogueItemLoincCode:
          ALLOWED_CATALOGUE_LOINC_CODES["typhoid-igg-igm-rapid-test"],
        displayName: "Typhoid IgG/IgM Rapid Test",
        code: "104294000",
      },
      {
        catalogueItemLoincCode:
          ALLOWED_CATALOGUE_LOINC_CODES["salmonella-antigen-test"],
        displayName: "Salmonella Antigen Test",
        code: "17562-0",
      },
      {
        catalogueItemLoincCode: ALLOWED_CATALOGUE_LOINC_CODES["widal-test"],
        displayName: "Widal test",
        code: "165828002",
      },
    ],
  },
  [Diseases.uti]: {
    id: Diseases.uti,
    diseaseDisplayName: "UTI",
    investigations: [
      {
        catalogueItemLoincCode: ALLOWED_CATALOGUE_LOINC_CODES.urinalysis,
        displayName: "Urinalysis",
        code: "167226008",
      },
    ],
  },
  [Diseases.dengue]: {
    id: Diseases.dengue,
    diseaseDisplayName: "Dengue fever",
    investigations: [
      {
        catalogueItemLoincCode:
          ALLOWED_CATALOGUE_LOINC_CODES["dengue-fever-antibody-test"],
        displayName: "Dengue Fever Antibody Test",
        code: "121993008",
      },
    ],
  },
};

export type DiseaseType = (typeof Diseases)[keyof typeof Diseases];

// Drawn up from this spreadsheet https://docs.google.com/spreadsheets/d/1DA-TkX7Ggsmrsns9OY9Pdpu_H9iNPC2Vlq9q2oDRoDQ/edit?usp=sharing
export const PositiveResultToSnomedCode = {
  positive: "10828004",
  "+": "10828004",
  "++": "10828004",
  "+++": "10828004",
  "haemolysis +": "1348319007",
  "haemolysis ++": "1348318004",
  "haemolysis +++": "1348320001",
  reactive: "10828004",
};

export const NegativeResultToSnomedCode = {
  negative: "260385009",
  invalid: "455371000124106",
  trace: "260385009",
  "haemolysis trace": "167299009",
  "non-haemolysis +": "1348270000",
  "non-haemolysis ++": "1348270000",
  "non-reactive": "260385009",
  inconclusive: "455371000124106",
};

export const AllResultsToSnomedCode = {
  ...PositiveResultToSnomedCode,
  ...NegativeResultToSnomedCode,
};

export const constructResourceId = (
  uri: string,
  patientId: string,
  submissionStatus: ClinicalResourceSubmissionStatus,
) => `${uri}-${patientId}-${submissionStatus}`;

const MALARIA_LOINC_CODES = {
  "malaria-rdt": "70569-9",
  "malaria-blood-smear-result": "32700-7-a",
};

export const ALLOWED_LOINC_CODES = {
  ...MALARIA_LOINC_CODES,
  "salmonella-antigen-test": "101140-2",
  "typhoid-igm": "17566-1-b",
  "urinalysis-nitrites": "5802-4",
  "urinalysis-leukocytes": "5799-2",
  "urinalysis-blood": "5794-3",
  "dengue-igg": "29661-6",
  "dengue-igm": "29663-2",
};

type ValidOrderedProperty = {
  investigationMeta: { coding: { loinc: { code: string } } };
  value: string;
};
export const mapInvestigationToFhirResources = ({
  investigation,
  patientId,
}: {
  investigation: InvestigationActivity;
  patientId: string;
}): PatientClinicalResource[] => {
  // Requested investigations
  // TODO: ENG-4803
  // Using catalogue loinc code, slightly different to how form investigation are
  //  mapped into patient resources, but equivalent atm for our purpose (filtering recommendations)
  if (!investigation?.outcome?.orderedProperties) {
    const catalogueLoincCode =
      investigation.catalogueItem.coding?.loinc?.code || "";

    if (
      !Object.values(ALLOWED_CATALOGUE_LOINC_CODES).includes(catalogueLoincCode)
    )
      return [];

    return [
      {
        id: constructResourceId(
          `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${catalogueLoincCode}`,
          patientId,
          ClinicalResourceSubmissionStatus.Submitted,
        ),
        meta: investigation.meta,
        patient: { id: patientId },
        submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
        fhirResource: {
          resourceType: FHIRResourceTypes["investigation-activity"],
          externalCoding: {
            icd10: null,
            icd11: null,
            snomed: null,
            loinc: {
              uri: `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${catalogueLoincCode}`,
            },
            elephantInternal: null,
          },
          diagnosticReport: {
            result: [],
          },
        },
      },
    ];
  }

  return investigation.outcome.orderedProperties
    .filter(
      (
        result: DeepPartial<ValidOrderedProperty>,
      ): result is ValidOrderedProperty =>
        !!result?.investigationMeta?.coding?.loinc?.code &&
        Object.values(ALLOWED_RESULT_LOINC_CODES).includes(
          result.investigationMeta.coding.loinc.code,
        ),
    )
    .map((result) => {
      if (!result.value) return null;
      const loincCode = result.investigationMeta.coding.loinc.code;
      const resultValue = result.value.trim().toLowerCase();
      const snomedCode = AllResultsToSnomedCode[resultValue];

      if (!snomedCode) return null;
      return {
        id: constructResourceId(
          `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${loincCode}`,
          patientId,
          ClinicalResourceSubmissionStatus.Submitted,
        ),
        meta: investigation.meta,
        patient: { id: patientId },
        submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
        fhirResource: {
          resourceType: FHIRResourceTypes["investigation-activity"],
          externalCoding: {
            icd10: null,
            icd11: null,
            snomed: null,
            loinc: {
              uri: `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${loincCode}`,
            },
            elephantInternal: null,
          },
          diagnosticReport: {
            result: [
              {
                value: {
                  externalCoding: {
                    snomed: {
                      uri: `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}${snomedCode}`,
                    },
                  },
                },
              },
            ],
          },
        },
      };
    })
    .filter((r) => !!r);
};

const isIcdOrLoincCodedResource = (resource: PatientClinicalResource) =>
  resource.fhirResource.externalCoding.icd11?.uri ||
  resource.fhirResource.externalCoding.loinc?.uri;

const getIcd11Uri = (resource: PatientClinicalResource) =>
  resource.fhirResource.externalCoding.icd11?.uri;

const isMalariaLoincUri = (uri: string | undefined) =>
  [
    `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${ALLOWED_LOINC_CODES["malaria-rdt"]}`,
    `${EXTERNAL_CODING_TO_URI_PREFIX.loinc}${ALLOWED_LOINC_CODES["malaria-blood-smear-result"]}`,
  ].includes(uri ?? "");

const hasPositiveResult = (resource: PatientClinicalResource) =>
  resource.fhirResource.diagnosticReport?.result?.some(
    (r) =>
      r.value?.externalCoding?.snomed?.uri ===
      `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}${PositiveResultToSnomedCode.positive}`,
  );

const hasNegativeResult = (resource: PatientClinicalResource) =>
  resource.fhirResource.diagnosticReport?.result?.some(
    (r) =>
      r.value?.externalCoding?.snomed?.uri ===
      `${EXTERNAL_CODING_TO_URI_PREFIX.snomed}${NegativeResultToSnomedCode.negative}`,
  );

export type ClinicalFindingsAccumulator = {
  bacterialDiagnoses: string[];
  malariaDiagnoses: string[];
  positiveMalarialResults: string[];
  negativeMalarialResults: string[];
  findings: FindingType[];
  investigationCatalogueLoincCodes: string[];
};

const isFindingType = (snomed: string | undefined): snomed is FindingType =>
  snomed ? new Set<string>(Object.values(Findings)).has(snomed) : false;

const extractLoincCode = (loincUri: string | undefined): string | null => {
  if (!loincUri) return null;
  const code = loincUri.replace(/https?:\/\/(www\.)?loinc\.org\//, "");
  return ALLOWED_RESULT_TO_CATALOGUE_LOINC_CODES[code] ?? code;
};

export const extractElephantInternalCode = (
  elephantInternalUri: string | undefined,
): string | null => {
  if (!elephantInternalUri) return null;
  const code = elephantInternalUri.replace(
    /http:\/\/elephant\.healthcare\//,
    "",
  );
  return code;
};

const isWithinGeneralRecencyWindow = (resource: PatientClinicalResource) => {
  return (
    new Date(resource.meta.updatedAt) >
    new Date(Date.now() - GENERAL_RECENCY_WINDOW_MS)
  );
};

export const shouldAddDiseaseToCDS = (code: string) => {
  return (
    isBacterialDiseaseIcd11Code(code) || isGuidedDiagnosisDiseaseIcd11Code(code)
  );
};

export const accumulateClinicalFindings = (
  acc: ClinicalFindingsAccumulator,
  resource: PatientClinicalResource,
) => {
  const newAcc = clone(acc);

  if (isIcdOrLoincCodedResource(resource)) {
    const icd11Uri = getIcd11Uri(resource);
    if (icd11Uri) {
      const icd11Code = icd11Uri.split("/").pop();
      if (icd11Code && isWithinGeneralRecencyWindow(resource)) {
        if (isBacterialDiseaseIcd11Code(icd11Code)) {
          newAcc.bacterialDiagnoses.push(icd11Uri);
        }
        if (isGuidedDiagnosisDiseaseIcd11Code(icd11Code)) {
          newAcc.malariaDiagnoses.push(icd11Uri);
        }
      }
    }

    const loincUri = resource.fhirResource.externalCoding.loinc?.uri;
    if (isMalariaLoincUri(loincUri) && isWithinGeneralRecencyWindow(resource)) {
      if (hasPositiveResult(resource) && loincUri) {
        newAcc.positiveMalarialResults.push(loincUri);
      }

      if (hasNegativeResult(resource) && loincUri) {
        newAcc.negativeMalarialResults.push(loincUri);
      }
    }

    const loincCode = extractLoincCode(loincUri);
    if (loincCode) {
      newAcc.investigationCatalogueLoincCodes.push(loincCode);
    }
  }

  if (resource.fhirResource.externalCoding.snomed?.uri) {
    const snomed = resource.fhirResource.externalCoding.snomed?.uri;
    if (isFindingType(snomed)) {
      newAcc.findings.push(snomed);
    }
  }

  return newAcc;
};
